import type {
  AssetRequirement,
  ManifestFragment
} from "@/app/integration/contracts";
import { toMp3FallbackPath } from "@/audio/resource-paths";
import { MATH_LEVELS, type MathLevel } from "@/content/math";

export const B4_MATH_MARKET_APPLE_ASSET =
  "/assets/english/vocabulary/apple.webp";

const B4_MATH_PLAYER_ASSET_OWNER_PREFIX = "b4-math-player-";

function createPromptAudioRequirements(
  level: MathLevel
): readonly AssetRequirement[] {
  if (level.promptAudio === undefined) {
    throw new Error("Missing prompt audio for " + level.id);
  }

  return [level.promptAudio, toMp3FallbackPath(level.promptAudio)].map(
    (path) => ({
      path,
      kind: "audio" as const,
      levelIds: [level.id]
    })
  );
}

function createLevelRequirements(
  level: MathLevel
): readonly AssetRequirement[] {
  const requirements: AssetRequirement[] = [
    ...createPromptAudioRequirements(level)
  ];

  if (level.id === "math-forest-market-1") {
    requirements.push({
      path: B4_MATH_MARKET_APPLE_ASSET,
      kind: "image",
      levelIds: [level.id]
    });
  }

  return requirements;
}

function createLevelManifest(level: MathLevel): ManifestFragment {
  return {
    owner: B4_MATH_PLAYER_ASSET_OWNER_PREFIX + level.id,
    requirements: createLevelRequirements(level)
  };
}

export const B4_MATH_PLAYER_ASSET_MANIFESTS: readonly ManifestFragment[] =
  MATH_LEVELS.map(createLevelManifest);

function getManifestLevelId(manifest: ManifestFragment): string {
  const levelIds = new Set(
    manifest.requirements.flatMap(({ levelIds }) => levelIds)
  );

  if (levelIds.size !== 1) {
    throw new Error(
      "B4 Math player asset manifest " +
        manifest.owner +
        " must declare exactly one level ID"
    );
  }

  const levelId = levelIds.values().next().value;

  if (levelId === undefined) {
    throw new Error("Missing B4 Math player asset manifest level ID");
  }
  if (
    manifest.requirements.some(
      ({ levelIds }) =>
        levelIds.length !== 1 || levelIds[0] !== levelId
    )
  ) {
    throw new Error(
      "B4 Math player asset manifest " +
        manifest.owner +
        " must only declare level " +
        levelId
    );
  }
  if (
    manifest.owner !== B4_MATH_PLAYER_ASSET_OWNER_PREFIX + levelId
  ) {
    throw new Error(
      "B4 Math player asset manifest owner " +
        manifest.owner +
        " does not match level " +
        levelId
    );
  }

  return levelId;
}

export function createB4MathPlayerAssetManifestLookup(
  expectedLevelIds: readonly string[],
  manifests: readonly ManifestFragment[]
): ReadonlyMap<string, ManifestFragment> {
  const expectedIds = new Set<string>();

  for (const levelId of expectedLevelIds) {
    if (expectedIds.has(levelId)) {
      throw new Error("Duplicate B4 Math level ID " + levelId);
    }
    expectedIds.add(levelId);
  }

  const manifestsByLevelId = new Map<string, ManifestFragment>();

  for (const manifest of manifests) {
    const levelId = getManifestLevelId(manifest);

    if (!expectedIds.has(levelId)) {
      throw new Error(
        "Unexpected B4 Math player asset manifest for " + levelId
      );
    }
    if (manifestsByLevelId.has(levelId)) {
      throw new Error(
        "Duplicate B4 Math player asset manifest for " + levelId
      );
    }

    manifestsByLevelId.set(levelId, manifest);
  }

  for (const levelId of expectedIds) {
    if (!manifestsByLevelId.has(levelId)) {
      throw new Error(
        "Missing B4 Math player asset manifest for " + levelId
      );
    }
  }

  return manifestsByLevelId;
}

const B4_MATH_PLAYER_ASSET_MANIFEST_BY_LEVEL =
  createB4MathPlayerAssetManifestLookup(
    MATH_LEVELS.map(({ id }) => id),
    B4_MATH_PLAYER_ASSET_MANIFESTS
  );

export function getB4MathPlayerAssetManifest(
  levelId: string
): ManifestFragment {
  const manifest = B4_MATH_PLAYER_ASSET_MANIFEST_BY_LEVEL.get(levelId);

  if (manifest === undefined) {
    throw new Error("Missing B4 Math player asset manifest for " + levelId);
  }

  return manifest;
}
